import {Box, Typography} from '@mui/material';
import {useEffect, useMemo, useState} from 'react';
import ArrDataGridPro from '../../../../common/arr-data-grid-pro/ArrDataGridPro';
import './DocErrorPanel.scss';
import {t} from 'i18next';
import ArrButton from '../../../../common/arr-button/ArrButton';
import {columns} from './DocErrorPanelData';
import {useGetAIReviewDocErrorPanel} from '../../../../../hooks/ai/review/useReview';
import {IAIReviewDocErrorPanel} from '../../../../../api/types/aiReview.type';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

type IRow = IAIReviewDocErrorPanel & {
  id: string;
};

const DocErrorPanel = () => {
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [errorFilter, setErrorFilter] = useState('');
  const [pageSize, setPageSize] = useState<number>(25);
  const [pageNumber, setPageNumber] = useState<number>(1);

  const {
    data: reviewDocErrorPanel,
    isLoading,
    isError,
  } = useGetAIReviewDocErrorPanel({
    page: pageNumber,
    pageSize,
    search: errorFilter,
    sortBy: 'docId',
    sortOrder: 'asc',
  });

  const rows: IRow[] = useMemo(() => {
    if (!reviewDocErrorPanel?.items) return [];

    return reviewDocErrorPanel.items.map((item) => ({
      ...item,
      id: item.docId,
    }));
  }, [reviewDocErrorPanel]);

  useEffect(() => {
    setPageNumber(1);
  }, [errorFilter]);

  return (
    <Box className="docs-errored-container">
      {/* Header */}
      <Box className="header">
        <Typography
          className="typo-text headline3_size"
          tabIndex={0}
          data-testid="doc-error-panel-title"
        >
          {t('ai.review.jobErrorPanel.title')}
        </Typography>

        <Box tabIndex={0}>
          <ArrButton
            variant="secondary"
            size="medium"
            disabled={selectedRows.length === 0}
            aria-label={t(
              'ai.review.groundTruthEvaluation.metrics.viewInDocList'
            )}
            dataTestId="viewInDocList-button"
          >
            {t('ai.review.groundTruthEvaluation.metrics.viewInDocList')}
          </ArrButton>
        </Box>
      </Box>

      <ArrDataGridPro
        className="docs-errored-grid"
        rows={rows}
        columns={columns({selectedRows, setSelectedRows, rows})}
        density="compact"
        showPerPageOptions
        autoHeight
        headerFilters
        pagination
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        itemsPerPage={pageSize}
        setItemsPerPage={setPageSize}
        onPageChange={setPageNumber}
        currentPage={pageNumber}
        disableColumnResize={false}
        rowHeight={60}
        rowCount={reviewDocErrorPanel?.total ?? 0}
        hideFooter={rows.length === 0}
        filterMode="server"
        paginationMode="server"
        disableColumnMenu
        loading={isLoading}
        onFilterModelChange={(model) => {
          const value = model.items?.[0]?.value || '';
          setErrorFilter(value);
        }}
        entityType={t('common.entries')}
      />

      {isError && (
        <Typography color="error" mt={2}>
          Failed to load data
        </Typography>
      )}
    </Box>
  );
};

export default DocErrorPanel;
